import { useMemo } from "react";
import { styled } from "../config/stiches.config";

export const GradeBox = styled("p", {
  fontFamily: "$inter",
  borderRadius: "$2",
  whiteSpace: "nowrap",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  maxWidth: "fit-content",
  variants: {
    size: {
      xsmall: {
        fontSize: "$1",
        lineHeight: "$1",
        fontWeight: "$1",
        height: "16px",
        py: "$3",
        px: "$3",
      },
      small: {
        fontSize: "$2",
        lineHeight: "$2",
        fontWeight: "$2",
        height: "32px",
        px: "6px",
      },
      medium: {
        fontSize: "$2",
        lineHeight: "$3",
        fontWeight: "$3",
        py: "$space$3",
        px: "$space$3",
        maxHeight: "40px",
      },
      large: {
        fontSize: "$3",
        lineHeight: "$3",
        fontWeight: "$3",
        px: "$3",
        py: "18px",
        maxHeight: "48px",
      },
    },
    colorScheme: {
      lime: {
        color: "$colors$lime700",
        backgroundColor: "$colors$lime200",
      },
      violet: {
        color: "$colors$violet700",
        backgroundColor: "$colors$violet200",
      },
      sunshine: {
        color: "$colors$sunshine700",
        backgroundColor: "$colors$sunshine200",
      },
      mango: {
        color: "$colors$mango600",
        backgroundColor: "$colors$mango100",
      },
      mangoDark: {
        color: "$colors$mango700",
        backgroundColor: "$colors$mango200",
      },
      red: {
        color: "$colors$red700",
        backgroundColor: "$colors$red200",
      },
      blue: {
        color: "$colors$ocean700",
        backgroundColor: "$colors$ocean200",
      },
      teal: {
        color: "$colors$teal600",
        backgroundColor: "$colors$teal200",
      },
    },
  },
});

type GradeSchemeProps = React.ComponentProps<typeof GradeBox>;

export function GradeScheme<
  T extends { low: number; high: number; color: string; letter: string }
>({
  grade,
  maxPoints,
  gradingScheme,
  css,
  isGraded,
  gradePercentage = -1,
  isAssignment = false,
  ...props
}: {
  grade?: number;
  maxPoints?: number;
  isGraded?: boolean;
  gradingScheme: {
    percentageGrade: string;
    ranges: T[];
  };
  gradePercentage?: number;
  isAssignment?: boolean;
} & GradeSchemeProps) {
  const { text, colorSchema } = useMemo(() => {
    return formatGradeText({
      grade,
      maxPoints,
      gradingScheme,
      isGraded,
      gradePercentage,
      isAssignment,
    });
  }, [grade, gradingScheme, maxPoints, selectGradeSchemeColor]);

  if (!text) return null;
  return (
    <GradeBox
      {...props}
      css={{ ...css, fontWeight: 500 }}
      colorScheme={(colorSchema as any) || "lime"}
    >
      {text || ""}
    </GradeBox>
  );
}

export function formatGradeText<
  T extends { low: number; high: number; color: string; letter: string }
>({
  maxPoints,
  gradingScheme,
  grade,
  isGraded,
  gradePercentage,
  isAssignment,
}: {
  grade?: number;
  maxPoints?: number;
  isGraded?: boolean;
  gradingScheme: {
    percentageGrade: string;
    ranges: T[];
  };
  gradePercentage?: number | string;
  isAssignment?: boolean;
}) {
  if (!maxPoints)
    return {
      text: "",
      colorSchema: "",
    };
  if (
    (typeof grade !== "number" || !(grade >= 0)) &&
    typeof gradePercentage !== "number"
  )
    return {
      text: "",
      colorSchema: "",
    };

  let maxSchema: T = gradingScheme?.ranges?.[0];
  let calculatedGrade = checkValue(gradePercentage);
  if (!calculatedGrade && Number(grade) >= 0 && isValidValue(grade)) {
    calculatedGrade = Number(((grade as number) / maxPoints) * 100).toFixed(0);
  }
  if (calculatedGrade === null || calculatedGrade === undefined) {
    return {
      text: "",
      colorSchema: "",
    };
  }

  const [schema] =
    gradingScheme?.ranges?.filter((s: any) => {
      if (maxSchema.high < s.high) {
        maxSchema = s;
      }
      return (
        s?.low <= Number(calculatedGrade) && s?.high >= Number(calculatedGrade)
      );
    }) ?? [];

  let letter;
  switch (true) {
    case !!schema:
      letter = schema.letter;
      break;
    case !!maxSchema?.high && Number(calculatedGrade) > maxSchema.high:
      letter = maxSchema.letter;
      break;
    default:
      break;
  }

  let pointsToShow = !!gradingScheme?.percentageGrade
    ? `${grade}/${maxPoints}`
    : grade;

  const text =
    isGraded || isAssignment
      ? `${calculatedGrade}% ${letter || ""}`
      : `${pointsToShow} points`;

  return {
    text: text,
    colorSchema: selectGradeSchemeColor(Number(calculatedGrade), true),
  };
}

function isValidValue(value: unknown): boolean {
  return (
    value !== null &&
    value !== undefined &&
    value !== "" &&
    Number(value) >= 0 &&
    !isNaN(value as number)
  );
}

type GradeSchemeColors =
  | "lime"
  | "violet"
  | "sunshine"
  | "mango"
  | "red"
  | "blue"
  | "mangoDark";

function selectGradeSchemeColor(
  grade: number,
  isGradePercentage: boolean
): GradeSchemeColors {
  if (!isGradePercentage) {
    if (grade >= 4) {
      return "lime";
    } else if (grade >= 3) {
      return "violet";
    } else if (grade >= 2) {
      return "sunshine";
    } else if (grade >= 1) {
      return "mango";
    } else {
      return "red";
    }
  }

  if (grade >= 90) {
    return "lime";
  } else if (grade >= 80) {
    return "violet";
  } else if (grade >= 70) {
    return "sunshine";
  } else if (grade >= 20) {
    return "mango";
  } else if (grade >= 1) {
    return "mangoDark";
  } else {
    return "red";
  }
}

/**
 * 
 * function getFixedColors(value: number, isGradePercentage: boolean) {
    if (!isGradePercentage) {
        if (value >= 4) {
            return 'bg-lime-700';
        } else if (value >= 3) {
            return 'bg-violet-700';
        } else if (value >= 2) {
            return 'bg-sunshine-700';
        } else if (value >= 1) {
            return 'bg-mango-600';
        } else {
            return 'bg-error-700';
        }
    }

    if (value >= 90) {
        return 'bg-lime-700';
    } else if (value >= 80) {
        return 'bg-violet-700';
    } else if (value >= 70) {
        return 'bg-sunshine-700';
    } else if (value >= 60) {
        return 'bg-mango-600';
    } else if (value >= 1) {
        return 'bg-mango-700';
    } else {
        return 'bg-error-700';
    }
}
 * 
 */

function checkValue(value?: string | number) {
  if (isValidValue(value)) return Number(value).toFixed(0);
  return null;
}
